import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Tooltip,
  Legend,
} from "chart.js"
import { Bar } from "react-chartjs-2"
import { colors } from "../../lib/colors"
import type { ChartData } from "../../types/analytics"
import { pickGranularity, buildAxisTickLabels, formatTooltipDate } from "../../lib/chartAxis"

ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip, Legend)

interface BarChartProps {
  data: ChartData
  color?: string
  horizontal?: boolean
  /** labels が ISO 日付（YYYY-MM-DD）のとき true。期間に応じて軸を日/月/年へ自動整形する（縦棒のみ） */
  dateAxis?: boolean
}

const BarChart = ({ data, color = colors.primary, horizontal = false, dateAxis = false }: BarChartProps) => {
  const multiDataset = data.datasets.length > 1

  const useDateAxis  = dateAxis && !horizontal
  const granularity  = useDateAxis ? pickGranularity(data.labels) : null
  const tickLabels   = granularity ? buildAxisTickLabels(data.labels, granularity) : null

  const chartData = {
    labels: data.labels,
    datasets: data.datasets.map(ds => ({
      ...ds,
      backgroundColor: ds.backgroundColor ?? color,
      borderRadius:    4,
    })),
  }

  return (
    <Bar
      data={chartData}
      options={{
        indexAxis:          horizontal ? "y" : "x",
        responsive:         true,
        maintainAspectRatio: false,
        plugins: {
          legend: { display: multiDataset, position: "top" as const },
          ...(useDateAxis && {
            tooltip: {
              callbacks: {
                title: (items: { dataIndex: number }[]) =>
                  items.length ? formatTooltipDate(data.labels[items[0].dataIndex]) : "",
              },
            },
          }),
        },
        scales: {
          x: {
            grid: { display: !horizontal },
            ticks: tickLabels
              ? {
                  font: { size: 11 },
                  autoSkip: granularity === "day",
                  maxTicksLimit: granularity === "day" ? 8 : undefined,
                  // category scale の第1引数はデータindex。autoSkipで間引かれてもズレないよう value を使う
                  callback: (value: string | number) => tickLabels[Number(value)] ?? "",
                }
              : { maxTicksLimit: 8, font: { size: 11 } },
          },
          y: { grid: { color: "#f3f4f6", display: horizontal }, ticks: { font: { size: 11 } } },
        },
      }}
    />
  )
}

export default BarChart
